/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComboboxItem } from "@mantine/core"
import { useThrottledCallback } from "@mantine/hooks"
import { DefaultSelectProps, DefaultSelect } from "../DefaultSelect"
import { useDefaultSuspenseInfiniteQuery } from "../../utils/useDefaultSuspenseInfiniteQuery"
import { Pagination } from "../../utils/Pagination"
import { useSelectedItemFallback } from "../../utils/useSelectedItemFallback"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query"

type ExtractType<TData> = TData extends Pagination<infer T> ? T : never

export interface SelectFromInfiniteQueryProps<
  TQueryData extends UseInfiniteQueryResult<InfiniteData<Pagination<TData>>>,
  TData = TQueryData extends ReturnType<
    | typeof useDefaultSuspenseInfiniteQuery<
        any,
        infer T extends Pagination<any>
      >
    | typeof useDefaultInfiniteQuery<any, infer T extends Pagination<any>>
  >
    ? ExtractType<T>
    : unknown,
> extends Omit<
  DefaultSelectProps,
  "options" | "searchValue" | "onSearchChange"
> {
  queryData: TQueryData
  entitiesToComboboxData: (data: TData) => ComboboxItem
  searchValue?: string
  onSearchChange?: (val: string) => void
  loading?: boolean
  /** Optional function to fetch a single item by ID. If provided, selected items will be fetched separately and merged into options. */
  fetchSelectedItem?: (id: string | number) => Promise<TData | null>
  /** Maps a string ID to the React Query key for that entity — must match the key used by the corresponding dataloader/query hook to share the cache. */
  getSelectedItemQueryKey?: (id: string) => readonly unknown[]
}

export function SelectFromInfiniteQuery<
  TQueryData extends UseInfiniteQueryResult<InfiniteData<Pagination<TData>>>,
  TData = TQueryData extends UseInfiniteQueryResult<
    InfiniteData<Pagination<infer U>>
  >
    ? U
    : unknown,
>({
  searchValue,
  onSearchChange,
  entitiesToComboboxData,
  loading,
  queryData,
  fetchSelectedItem,
  getSelectedItemQueryKey,
  ...rest
}: SelectFromInfiniteQueryProps<TQueryData, TData>) {
  const [internalSearchValue, setInternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(() => {
    setInternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
    queryData

  // Fetch selected item(s) separately if a fetch function is provided
  const { selectedComboboxItems, isLoadingSelectedItems } =
    useSelectedItemFallback({
      selectedIds: rest.value,
      entitiesToComboboxData,
      fetchSelectedItem,
      getQueryKey: getSelectedItemQueryKey ?? ((id) => ["selectedItem", id]),
    })

  const throttledFetchNextPage = useThrottledCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, 300)

  const handleSearchValueChange = (newVal: string) => {
    onSearchChange?.(newVal)
    setInternalSearchValue(newVal)
  }

  // Merge paginated options with selected items
  // If a selected item is not in the current page, prepend it
  const paginatedOptions =
    data?.pages.flatMap((page) =>
      page?.data.flatMap((entity) => entitiesToComboboxData(entity))
    ) ?? []

  const mergedOptions = React.useMemo(() => {
    if (selectedComboboxItems.length === 0) {
      return paginatedOptions
    }

    // Create a Set of IDs in paginated options for O(1) lookup
    const paginatedIds = new Set(paginatedOptions.map((opt) => opt.value))

    // Add selected items that are not already in paginated options (prepend)
    const newItems = selectedComboboxItems.filter(
      (item: ComboboxItem) => !paginatedIds.has(item.value)
    )

    return newItems.length > 0
      ? [...newItems, ...paginatedOptions]
      : paginatedOptions
  }, [paginatedOptions, selectedComboboxItems])

  return (
    <DefaultSelect
      searchValue={internalSearchValue}
      onSearchChange={handleSearchValueChange}
      options={mergedOptions}
      value={rest.value}
      loading={isLoading || isLoadingSelectedItems || loading}
      onScrolledToBottom={throttledFetchNextPage}
      {...rest}
    />
  )
}
